import { Coordinate } from "client/Misc/commonTypes";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	memo,
	MouseEvent,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { VisibilityActionButton, VisibilityActionState } from "./VisibilityActionButton";
import { ReorderableList } from "client/Components";
import { VisibilityItemContent } from "./VisibilityItemContent";
import { VisibilityActionMenu } from "./VisibilityActionMenu";
import styles from "./VisibilityRootNode.module.less";
import {
	setVisibilityRecursive,
	visibleUnderSearchTerm,
	VisibilityNodeData
} from "./common";
import classNames from "classnames";
import { NormalizedSearchTerm } from "client/Data/searching";

export type VisibilityRootNodeProps = {
	data: VisibilityNodeData;
	onChange: (changedRootNode: VisibilityNodeData) => void;
	searchTerm: NormalizedSearchTerm;
};

export const VisibilityRootNode = memo(
	(props: VisibilityRootNodeProps): ReactElement => {
		const {
			data,
			onChange,
			searchTerm
		} = props;

		const [contextMenuPosition, setContextMenuPosition] = useState<Coordinate | null>(null);

		const visibilityActionButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

		const childListChangeHandler = useCallback(
			(items: readonly VisibilityNodeData[]): void => {
				onChange({
					...data,
					isVisible: data.isVisible
						|| items.some(x => x.isVisible),
					children: items
				});
			},
			[data, onChange]
		);

		const toggleVisibilityHandler = (): void => {
			onChange(
				setVisibilityRecursive(
					data,
					!data.isVisible,
					searchTerm
				)
			);
		};

		const trackingId: string = generateTrackingId("sharepoint-site", `|${data.id}`);

		const visibilityState: VisibilityActionState = data.isVisible
			? "visible"
			: "hidden";

		const visibleChildren = useMemo(
			(): readonly VisibilityNodeData[] => {
				return data.children
					.filter(x => visibleUnderSearchTerm(x, searchTerm));
			},
			[data.children, searchTerm]
		);

		return (
			<>
				<ReorderableList.Group
					headerContent={(
						<VisibilityItemContent
							data={data}
							level={0}
							isRoot
							onChange={onChange}
							hasVisibleChildren={visibleChildren.length > 0}
						/>
					)}
					actions={(
						<VisibilityActionButton
							ref={visibilityActionButtonRef}
							state={visibilityState}
							onClick={toggleVisibilityHandler}
						/>
					)}
					trackingId={trackingId}
					isContentInteractable
					noSpaceAroundHeaderContent
					mouseInteraction={{
						onContextMenu: setContextMenuPosition
					}}
					isActive={isNotNil(contextMenuPosition)}
				>
					{!data.isCollapsed && (
						<List
							items={data.children}
							onChange={childListChangeHandler}
							level={0}
							visibleItems={visibleChildren}
							searchTerm={searchTerm}
						/>
					)}
				</ReorderableList.Group>
				{isNotNil(contextMenuPosition) && (
					<VisibilityActionMenu
						target={ensureNotNil(visibilityActionButtonRef.current)}
						trackingId={generateTrackingId(trackingId, "__menu")}
						visibilityState={visibilityState}
						onToggleVisibility={toggleVisibilityHandler}
						onClose={() => setContextMenuPosition(null)}
						position={contextMenuPosition}
					/>
				)}
			</>
		);
	}
);
VisibilityRootNode.displayName = "VisibilityRootNode";

type ListProps = {
	items: readonly VisibilityNodeData[];
	onChange: (items: readonly VisibilityNodeData[]) => void;
	visibleItems: readonly VisibilityNodeData[];
	level: number;
	searchTerm: NormalizedSearchTerm;
};

const List = memo(
	(props: ListProps): ReactElement => {
		const {
			items,
			level,
			onChange,
			visibleItems,
			searchTerm
		} = props;

		const onChildNodeChanged = useCallback(
			(data: VisibilityNodeData): void => {
				const childIndex: number = items.findIndex((x) => x.id === data.id);

				if (childIndex === -1) {
					throw new Error(`Node was not identified as child '${JSON.stringify(data)}'`);
				}

				const copy: VisibilityNodeData[] = [...items];
				copy.splice(childIndex, 1, data);

				onChange(copy);
			},
			[items, onChange]
		);

		return (
			<ul>
				{visibleItems.map((x) => (
					<li key={x.id}>
						<Node
							item={x}
							level={level}
							onChange={onChildNodeChanged}
							searchTerm={searchTerm}
						/>
					</li>
				))}
			</ul>
		);
	}
);
List.displayName = "List";

type NodeItemProps = {
	item: VisibilityNodeData;
	level: number;
	onChange: (data: VisibilityNodeData) => void;
	searchTerm: NormalizedSearchTerm;
};

const Node = memo(
	(props: NodeItemProps): ReactElement => {
		const {
			item,
			level,
			onChange,
			searchTerm
		} = props;

		const [contextMenuPosition, setContextMenuPosition] = useState<Coordinate | null>(null);
		const visibilityActionButtonRef: React.RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

		const onChildListChange = useCallback(
			(items: readonly VisibilityNodeData[]): void => {
				onChange({
					...item,
					isVisible: item.isVisible || items.some(x => x.isVisible),
					children: items
				});
			},
			[item, onChange]
		);

		const visibilityState: VisibilityActionState = item.isVisible
			? "visible"
			: "hidden";

		const toggleVisibilityHandler = (): void => {
			onChange(
				setVisibilityRecursive(
					item,
					!item.isVisible,
					searchTerm
				)
			);
		};

		const contextMenuHandler = (args: MouseEvent): void => {
			args.preventDefault();

			setContextMenuPosition({
				x: args.clientX,
				y: args.clientY
			});
		};

		const closeContextMenuHandler = useCallback(
			(): void => {
				setContextMenuPosition(null);
			},
			[]
		);

		const trackingId: string = generateTrackingId("item", `|${item.id}`);

		const visibleChildren = useMemo(
			(): readonly VisibilityNodeData[] => {
				return item.children
					.filter(x => visibleUnderSearchTerm(x, searchTerm));
			},
			[item.children, searchTerm]
		);

		return (
			<>
				<div
					{...setTrackingId(trackingId)}
					className={
						classNames(
							styles.listItem,
							styles.listItem_hoverable,
							{[styles.listItem_active]: isNotNil(contextMenuPosition)}
						)
					}
					onContextMenu={contextMenuHandler}
				>
					<VisibilityItemContent
						data={item}
						level={level}
						onChange={onChange}
						hasVisibleChildren={visibleChildren.length > 0}
					/>
					<VisibilityActionButton
						ref={visibilityActionButtonRef}
						state={visibilityState}
						onClick={toggleVisibilityHandler}
					/>
				</div>
				{!item.isCollapsed && (
					<List
						items={item.children}
						onChange={onChildListChange}
						level={level + 1}
						visibleItems={visibleChildren}
						searchTerm={searchTerm}
					/>
				)}
				{isNotNil(contextMenuPosition) && (
					<VisibilityActionMenu
						target={ensureNotNil(visibilityActionButtonRef.current)}
						onClose={closeContextMenuHandler}
						visibilityState={visibilityState}
						onToggleVisibility={toggleVisibilityHandler}
						position={contextMenuPosition}
						trackingId={generateTrackingId(trackingId, "__menu")}
					/>
				)}
			</>
		);
	}
);
Node.displayName = "Node";
